import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import moment from "moment-timezone";
import debounce from "lodash/debounce";
import { Alert, Autocomplete, Box, CircularProgress, Divider, Drawer, IconButton, Popover, TextField, Tooltip, Typography, Collapse } from "@mui/material";
import { BusinessCenter, Close, Square, WebAsset, OpenInNew, IosShare, ContentCopy, Send, InfoOutlined, Help } from "@mui/icons-material";
import "./EventDetails.css";
import { StyledRating } from "../../ListView/ListView";
import { getAdskEmpList, getReleaseDetails, shareRelease, toggleUpvote, getUserIdFromPreference, fetchGenAiSummary, getServiceId } from "../../../Services";
import Loader from "../Loader/Loader";
import ThumbUpOffAltIcon from '@mui/icons-material/ThumbUpOffAlt';
import { Warning } from '@mui/icons-material';
import { eventTypes, CUSTOM_SAP_SERVICES } from "../../../utility/consts";
import OriginalSummaryAccordion from './OriginalSummaryAccordion';
import { shareReleaseData } from "../../../utility/shareRelease";

const EventDetails = ({ isDataLoaded, event, isOpen, setIsModalOpen, onClose }) => {
    const navigate = useNavigate();

    const [isDraweropen, setIsDrawerOpen] = useState(isOpen);
    const [isLoading, setLoader] = useState(true);
    const [details, setDetails] = useState({});
    const [history, setHistory] = useState();
    const [shareLink, setShareLink] = useState("");
    const [helperText, setHelperText] = useState("Copy Link");
    const [anchorEl, setAnchorEl] = useState(null);
    const [loading, setLoading] = useState(false);
    const [users, setUsers] = useState([]);
    const [formData, setFormData] = useState([]);
    const [isSubmit, setSubmit] = useState(false);
    const [error, setError] = useState("");
    const [additionalData, setAdditionalData] = useState(null);
    const [upvoteCount, setUpvoteCount] = useState(0);
    const [hasUpvoted, setHasUpvoted] = useState(false);
    const [genaiSummary, setGenaiSummary] = useState('');
    const [isLoaded, setLoaded] = useState(false);
    const typingStarted = useRef(false);
    const [serviceId, setServiceId] = useState(null);

    const open = Boolean(anchorEl);
    const id = open ? 'simple-popover' : undefined;
    const options = {
        position: "top-right",
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        autoClose: 2000,
        progress: undefined,
        theme: "light",
    };

    useEffect(() => {
        const getGenAiSummary = async () => {
            if (event.Vendor_type !== 'AWS Health') return;

            setLoaded(true);
            setError("");
            setGenaiSummary('');

            try {
                const response = await fetchGenAiSummary(event['feature_delivered']);

                if (!typingStarted.current) {
                    typingStarted.current = true;
                    displayTypingEffect(response);
                }
            } catch (error) {
                setError("Failed to load GenAI summary");
            } finally {
                setLoaded(false);
            }
        };

        if (isOpen) {
            getGenAiSummary();
        }
        else {
            typingStarted.current = false;
        }
    }, [isOpen]);

    const displayTypingEffect = (summary) => {
        let currentText = '';
        let index = 0;
        const batchSize = 10;
        const updateInterval = 90;

        const typeBatch = () => {
            if (index < summary.length) {
                currentText += summary.slice(index, index + batchSize);
                setGenaiSummary(currentText);
                index += batchSize;
                setTimeout(typeBatch, updateInterval);
            }
        };
        typeBatch();
    };

    useEffect(() => {
        if (isOpen && event) {
            fetchUpvoteData(event["release_id"], event["vendor_service"]);
            fetchServiceId(event["vendor_service"]);
        }
    }, [event, isOpen]);

    const fetchServiceId = async (businessService) => {
        try {
            const response = await getServiceId(businessService);
            if (response.data) {
                setServiceId(response.data);
            }
        } catch (error) {
            console.error("Error fetching Service ID:", error);
        }
    };

    const fetchUpvoteData = async (releaseId, service) => {
        try {
            const response = await getReleaseDetails(releaseId, service);

            if (response && response.data) {
                const responseData = response.data;
                const upvoteData = responseData.upvote_data;
                const userId = String(getUserIdFromPreference());
                setUpvoteCount(upvoteData.upvote_count);
                setHasUpvoted(upvoteData.upvoted_by.includes(userId));
            }
        } catch (error) {
            toast.error("Failed to fetch upvote data!", options);
        }
    };

    const handleUpvoteClick = debounce(async () => {
        const updatedHasUpvoted = !hasUpvoted;
        const updatedUpvoteCount = updatedHasUpvoted ? upvoteCount + 1 : upvoteCount - 1;

        setHasUpvoted(updatedHasUpvoted);
        setUpvoteCount(updatedUpvoteCount);

        try {
            const body = { release_id: event.release_id, user_id: String(getUserIdFromPreference()) };
            const response = await toggleUpvote(body);

            if (!response || !response.data.success) {
                setHasUpvoted(!updatedHasUpvoted);
                setUpvoteCount(updatedHasUpvoted ? updatedUpvoteCount - 1 : updatedUpvoteCount + 1);
                toast.error("Failed to toggle upvote!", options);
            } else {
                toast.success(response.data.message, options);
            }
        } catch (error) {
            console.log('Error toggling upvote:', error);
            setHasUpvoted(!updatedHasUpvoted);
            setUpvoteCount(updatedHasUpvoted ? updatedUpvoteCount - 1 : updatedUpvoteCount + 1);
            toast.error("Failed to toggle upvote!", options);
        }
    }, 400);

    useEffect(() => {
        setIsDrawerOpen(isOpen);
        if (isOpen && event) {
            if (event["additional_details"]) {
                setAdditionalData(JSON.parse(event["additional_details"]));
            }
            window.history.pushState(null, "", `/${event.release_id}`);
            setLoader(true);
            getReleaseDetailsForEvent(event["release_id"], event["vendor_service"]);
        }
    }, [event, isOpen]);

    const toggleDrawer = (event, bool) => {
        setIsDrawerOpen(bool);
        setIsModalOpen(event, bool);
        if (!bool) {
            setDetails({});
            setHistory(null);
        }
    };

    const getReleaseDetailsForEvent = async (id, service) => {
        try {
            const response = await getReleaseDetails(id, service);
            if (response && response.data) {
                let data = response.data;
                setDetails(data);
                if (data["history"]) {
                    // Sorting the release history based on dates in descending order.
                    const dataArray = Object.entries(data["history"]);
                    dataArray.sort((a, b) => moment(b[1].updatedAt) - moment(a[1].updatedAt));
                    const sortedData = Object.fromEntries(dataArray);
                    setHistory(sortedData);
                }
            } else {
                toast.error("Error while getting release details!!", options);
            }
            setLoader(false);
        } catch (error) {
            console.log(error?.message);
            setLoader(false);
            if (
                error?.response?.status === 401 ||
                error?.response?.data?.message === "User authorization failed."
            ) {
                navigate("/logout");
            } else {
                navigate("/error");
            }
        }
    };

    const getEmpData = async (searchStr) => {
        try {
            const response = await getAdskEmpList(searchStr);
            if (response.status === 200 && response && response.data) {
                setUsers(response.data.users);
                setLoading(false)
            }

        } catch (error) {
            console.log(error?.message);
            if (
                error?.response?.status === 401 ||
                error?.response?.data?.message === "User authorization failed."
            ) {
                navigate("/logout");
            } else {
                toast.error("Error while getting Employee List!!", options);
                navigate("/error");
            }
            setLoading(false)
        }
    };

    const sendQuery = useCallback((value) => {
        getEmpData(value);
    }, []);

    const debouncedSendQuery = useMemo(() => {
        return debounce(sendQuery, 1500);
    }, [sendQuery]);

    const handleInputChange = (e) => {
        const { value } = e.target;
        if (value && value.length >= 3) {
            setLoading(true)
            debouncedSendQuery(value);
        }
    };

    const handleEmployeeData = (options, value) => {
        setFormData(value);
    };

    const handleCopyClick = () => {
        navigator.clipboard.writeText(shareLink);
        setHelperText('Link Copied!');
    };

    const shareReleaseDetails = () => {
        setSubmit(true);
    }

    useEffect(() => {
        if (isSubmit) {
            console.log(isSubmit);
            if (Object.keys(formData).length > 0) {
                setError("");
                closePopover();
                const empList = formData.map(e => e["email"]);
                shareReleaseDataLocal(empList);
            } else {
                setError("Employee Name is Required!")
            }
            setSubmit(false);
        }
    }, [isSubmit]);

    useEffect(() => {
        setShareLink(`${window.location.origin}/${event.release_id}`);
    }, [event?.release_id]);

    const openPopover = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const closePopover = () => {
        setAnchorEl(null);
        setFormData([]);
    };

    const shareReleaseDataLocal = async (emp) => {
        await shareReleaseData(event, emp, shareLink);
    }
    const formatDateTimeUTC = (dateString) => {
        const date = moment.utc(dateString);
        if (!date.isValid()) return "N/A";
        return `${date.format("MMM DD, YYYY")} - ${date.format("HH:mm")} UTC`;
    };

    const renderAdditionalData = () => {
        const getStatusIcon = (plannedStartDate, status) => {
            const startDate = moment(plannedStartDate);
            if (isNaN(startDate)) return "N/A";

            const isCancelled = status === 'CANCELLED';
            const isExpiringSoon = moment().isSameOrAfter(startDate);
            return (
                <div className="d-flex align-items-center">
                    {(isCancelled || isExpiringSoon) && (
                        <Warning
                            className={`mr-1 ${isCancelled ? "text-danger" : "text-warning"}`}
                            fontSize="small"
                        />
                    )}
                    <span className={`text-s ${isCancelled ? "text-danger" : isExpiringSoon ? "text-black" : ""}`}>
                        {isCancelled ? "Expired" : isExpiringSoon ? "Expiring Soon" : "Open"}
                    </span>
                </div>
            );
        };
        const isValidUrl = (value) => {
            const urlPattern = /^(https?|ftp):\/\/[^\s/$.?#].[^\s]*$/i;
            return urlPattern.test(value);
        };

        const renderLink = (url, label) => {
            return (
                <a
                    className="text-s"
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    {label} <OpenInNew className="text-blue ml-1" fontSize="xs" />
                </a>
            );
        };

        return (
            additionalData && Object.entries(additionalData).filter(([key]) => key !== "ITSM Affinity").map(([key, value]) => (
                <div className="col-6 mb-3" key={key}>
                    <p className="mb-0 text-sm text-capitalize">
                        <strong>{key.replace(/_/g, " ")}</strong>
                    </p>
                    {value.length === 19 && moment(value, moment.ISO_8601, true).isValid() ? (
                        <span className="text-s">{moment(value).format("MMM DD, YYYY  HH:mm:SS")}</span>
                    ) : key.toLowerCase() === 'rfc_details' && value.length > 3 ? (
                        <div>
                            {value.split(', ').map((rfc) => (
                                <a
                                    className="text-s d-flex align-items-center"
                                    key={rfc}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    href={`https://autodesk-enterprise.wolkenservicedesk.com/wolken/esd/dynamic_filter?id=${rfc.slice(3)}`}
                                >
                                    {rfc} <OpenInNew className="text-blue ml-1" fontSize="xs" />
                                </a>
                            ))}
                        </div>
                    ) : key.toLowerCase() === 'status' ? (
                        getStatusIcon(event.planned_start_date, value)
                    ) : key.toLowerCase() === 'backout_plan' ? (
                        isValidUrl(value) ? renderLink(value, "View Backout Plan") : <span className="text-s">{value || "N/A"}</span>
                    ) : key.toLowerCase() === 'implementation_plan' ? (
                        isValidUrl(value) ? renderLink(value, "View Implementation Plan") : <span className="text-s">{value || "N/A"}</span>
                    ) : (
                        <span className="text-s">{value || "N/A"}</span>
                    )}
                </div>
            ))
        );
    };

    return (
        isOpen &&
        (isLoading ? (
            <Loader />
        ) : (
            <Drawer
                anchor="right"
                open={isDraweropen}
                onClose={() => {
                    toggleDrawer(null, false);
                    window.history.pushState(null, "", "/");
                }}
            >
                <div className="d-flex justify-content-between align-items-start">
                    <span className="top-tag">{event.vendor_service}</span>
                    <div className="d-flex align-items-center">
                        <p className="py-1 px-2 my-2 text-s d-flex align-items-center">
                            <Tooltip className="mr-2" title={'You have selected this business service in your preference which has this as its service component or dependent service in service registry.'}>
                                <Help sx={{ fontSize: 15 }} />
                            </Tooltip>
                            <strong>Why am I seeing this?</strong>
                        </p>
                        <IconButton
                            className="p-1 mx-2"
                            onClick={() => {
                                toggleDrawer(null, false);
                                window.history.pushState(null, "", "/");
                                if (typeof onClose === "function") {
                                    onClose();
                                }
                            }}
                        >
                        <Close />
                        </IconButton>
                    </div>
                </div>
                <Box sx={{ width: 700, pl: 4, pr: 3, fontSize: "13px" }}>
                    <div className="mb-3">
                        <div className="text-secondary d-flex text-s">
                            <span className="">{event.release_id}</span>
                            <h2 className="dot-content mx-2">&#183;</h2>
                            <span className="mr-1">Status: </span>
                            <span
                                className={
                                    event.release_status.toUpperCase() === "COMPLETED"
                                        ? "text-success"
                                        : event.release_status.toUpperCase() === "PLANNED"
                                            ? "text-secondary"
                                            : "text-info"
                                }
                            >
                                {event.release_status}
                            </span>
                            <h2 className=" dot-content mx-2">&#183;</h2>
                            <span className="">
                                {" "}
                                Last updated on{" "}
                                {moment(event.last_updated, moment.ISO_8601, true).isValid()
                                    ? moment(event.last_updated).format("MMM DD YYYY")
                                    : "N/A"}
                            </span>
                        </div>
                        <h5 className="font-weight-bold mb-2">
                            {event.title || event.release_title}
                        </h5>
                        <div className="d-flex align-items-center text-sm">
                            <a className="d-flex align-items-center text-primary cursor-pointer"
                                onClick={openPopover}
                                aria-describedby={id}
                                rel="noreferrer">
                                <IosShare className="text-blue mr-1" fontSize="xs" />
                                Share
                            </a>
                            <Popover
                                id={id}
                                open={open}
                                anchorEl={anchorEl}
                                onClose={closePopover}
                                anchorOrigin={{
                                    vertical: 'bottom',
                                    horizontal: 'left',
                                }}
                            >
                                <div className="share d-flex align-items-center px-3 py-2">
                                    <Autocomplete
                                        multiple
                                        id="employee"
                                        freeSolo
                                        className="w-350"
                                        loading={loading}
                                        getOptionLabel={(option) => option.displayName}
                                        filterOptions={(x) => x}
                                        onChange={(event, value) => handleEmployeeData(event, value)}
                                        options={users}
                                        key={option => option.email}
                                        renderOption={(props, option) => (
                                            <Typography title={option.email} sx={{ fontSize: '0.9rem' }} {...props}>
                                                {option.displayName}
                                            </Typography>)
                                        }
                                        renderInput={(params) => <TextField
                                            {...params}
                                            autoFocus
                                            margin="dense"
                                            id="name"
                                            width="200"
                                            label="Enter Autodesk Username"
                                            InputProps={{
                                                ...params.InputProps,
                                                endAdornment: (
                                                    <React.Fragment>
                                                        {loading ? <CircularProgress color="inherit" size={20} /> : null}
                                                        {params.InputProps.endAdornment}
                                                    </React.Fragment>
                                                ),
                                            }}
                                            required
                                            type="text"
                                            fullWidth
                                            variant="standard"
                                            name='employee'
                                            error={error.length > 0 && formData.length <= 0}
                                            helperText={error.length > 0 && formData.length <= 0 ? error : ""}
                                            value={formData || ''}
                                            onChange={handleInputChange}
                                        />}
                                    />
                                    <Tooltip title="Send" arrow>
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-outline-secondary active mx-2 mt-4"
                                            onClick={shareReleaseDetails}>
                                            <Send fontSize="xs" />
                                        </button>
                                    </Tooltip>
                                    <Tooltip title={helperText} arrow>
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-outline-secondary mt-4"
                                            onClick={handleCopyClick}>
                                            <ContentCopy fontSize="xs" />
                                        </button>
                                    </Tooltip>
                                </div>
                            </Popover>
                            <div className="d-flex align-items-center ml-4">
                                {event.Vendor_type === "Microsoft 365" ||
                                    event.release_url === "N/A" ||
                                    event.release_url === "NA" ? (
                                    <Tooltip title="Release details not available" arrow>
                                        <a className="text-secondary" rel="noreferrer">
                                            <OpenInNew disabled sx={{ color: 'grey !important', pointerEvents: 'none' }} className="text-blue mr-1" fontSize="xs" />
                                            View Details
                                        </a>
                                    </Tooltip>
                                ) : (
                                    <a
                                        href={event.release_url ? (event.Vendor_type === "Venafi Certificates" ? `https://certs.autodesk.com/aperture/tls-protect/certificate/${event.release_url}` : event.release_url) : event.release_id.startsWith("CHG") ? `https://autodesk-enterprise.wolkenservicedesk.com/wolken/esd/dynamic_filter?id=${event.release_id.slice(3)}` : event.release_id.startsWith("COE") ? `https://autodeskcloudops.servicenowservices.com/nav_to.do?uri=task.do?sysparm_query=number=${event.release_id}` : undefined}
                                        target="_blank"
                                        className="d-flex align-items-center text-primary cursor-pointer"
                                        rel="noreferrer"
                                    >
                                        <OpenInNew className="text-blue mr-1" fontSize="xs" />
                                        View Details
                                    </a>
                                )}
                            </div>
                        </div>
                    </div>
                    <Alert
                        severity="info"
                        icon={false}
                        action={
                            <Box className="d-flex justify-content-center align-items-center position-relative px-2">
                                <Typography
                                    variant="body2"
                                    className="text-xs d-flex align-items-center mr-2 text-muted">
                                    {upvoteCount > 0 && (
                                        hasUpvoted
                                            ? upvoteCount === 1
                                                ? 'Marked helpful by you'
                                                : upvoteCount === 2
                                                    ? 'Marked helpful by you and 1 other'
                                                    : `Marked helpful by you and ${upvoteCount - 1} others`
                                            : upvoteCount === 1
                                                ? 'Marked helpful by 1 other'
                                                : `Marked helpful by ${upvoteCount} others`
                                    )}
                                </Typography>
                            </Box>
                        }
                        className="alert d-flex align-items-center px-2"
                        sx={{
                            bgcolor: '#F7F6FB',
                            height: 40,
                            '.MuiAlert-action': { p: 0 },
                            '.MuiAlert-message': { display: 'contents' }
                        }}>
                        <Typography
                            variant="body2"
                            className="text-s text-body-color" >
                            Was this data helpful?
                        </Typography>
                        <IconButton
                            aria-label="thumbs up"
                            className="ml-3 px-1 position-relative rounded-circle"
                            sx={{
                                backgroundColor: hasUpvoted ? 'black' : 'gray',
                                color: 'white',
                                '&:hover': { backgroundColor: hasUpvoted ? 'dimgray' : 'lightgray' },
                                width: '20px',
                                height: '20px',
                            }}
                            onClick={handleUpvoteClick}>
                            <ThumbUpOffAltIcon className="text-sm" />
                        </IconButton>
                    </Alert>
                    <div className="row mt-3">
                        <div className="col-6 mb-3">
                            <p className="mb-1 text-sm">
                                <strong>Business Service</strong>
                            </p>
                            <div className="d-flex align-items-start">
                                <WebAsset fontSize="small" className="mr-1" />
                                {serviceId ?
                                    <a className="text-s" target="_blank" href={`https://one.autodesk.com/apps/service-registry/service-components?serviceId=${serviceId}`}>
                                        {event.vendor_service}{serviceId ? ` (${serviceId})` : ''}
                                    </a> :
                                    <span className="text-s">
                                        {event.vendor_service}
                                    </span>
                                }
                                {event.businessOwner &&
                                    <Tooltip className="ml-1" title={`Business Owner: ${event.businessOwner}`}>
                                        <InfoOutlined sx={{ fontSize: 15, marginTop: '1px' }} />
                                    </Tooltip>
                                }
                            </div>
                        </div>
                        <div className="col-6 mb-3">
                            <p className="mb-1 text-sm">
                                <strong>
                                    {event.Vendor_type === "AWS Health"
                                        ? "Affected Resources"
                                        : "Impacted Vendor Service"}
                                </strong>
                            </p>
                            <div className="d-flex align-items-start">
                                <WebAsset fontSize="small" className="mr-1" />
                                <span className="text-s-nowrap">{event.impacted_services}</span>
                            </div>
                        </div>
                        <div className="col-6 mb-3">
                            <p className="mb-1 text-sm">
                                <strong>Source</strong>
                            </p>
                            <div className="d-flex align-items-center">
                                <BusinessCenter fontSize="small" className="mr-1" />
                                <span className="text-s">{event.Vendor_type}</span>
                            </div>
                        </div>
                        <div className="col-6 mb-3">
                            <p className="mb-1 text-sm d-flex align-items-center">
                                <strong>Service Tier</strong>
                            </p>
                            <span className="text-s">{event.tier?.replace(/(TIER)(\d)/, '$1 $2') || 'N/A'}</span>
                        </div>
                        <div className="col-6 mb-3">
                            <p className="mb-1 text-sm">
                                <strong>Starts At</strong>
                            </p>
                            <span className="text-s">
                                {formatDateTimeUTC(event.planned_start_date)}
                            </span>
                        </div>
                        <div className="col-6 mb-3">
                            <p className="mb-1 text-sm">
                                <strong>
                                    {
                                        (["On-Prem DB", "Venafi Certificates"].includes(event.Vendor_type) ||
                                            (event.Vendor_type === "AWS Health" && event.title?.startsWith("ACM Certificate")))
                                            ? "Expires At"
                                            : ["Firewall/Security Exception"].includes(event.Vendor_type)
                                                ? "Due At" : "Ends At"
                                    }
                                </strong>

                            </p>
                            <span className={`text-s`}>
                                {formatDateTimeUTC(event.planned_end_date)}
                            </span>
                        </div>
                        <div className="col-6 mb-3">
                            <p className="mb-1 text-sm d-flex align-items-center">
                                <strong>Category</strong>
                                <Tooltip className="ml-1" title={'This value is determined based on the data available. It is advised to validate this once manually.'}>
                                    <InfoOutlined sx={{ fontSize: 15 }} />
                                </Tooltip>
                            </p>
                            <span className="text-s">{event.release_category || "N/A"}</span>
                        </div>
                        <div className="col-6 mb-3">
                            <p className="mb-1 text-sm">
                                <strong>Act By</strong>
                            </p>
                            <span className={`text-s`}>
                                {moment(event.act_by, moment.ISO_8601, true).isValid()
                                    ? moment(event.act_by).format("MMM DD YYYY")
                                    : "N/A"}
                            </span>
                        </div>
                        {
                            ["On-Prem DB", "Venafi Certificates"].includes(event.Vendor_type) ? renderAdditionalData() : null
                        }
                        <div className="col-6 mb-3">
                            <p className="mb-1 text-sm d-flex align-items-center">
                                <strong>Impact</strong>
                                <Tooltip className="ml-1" title={'This value is determined based on the data available. It is advised to validate this once manually.'}>
                                    <InfoOutlined sx={{ fontSize: 15 }} />
                                </Tooltip>
                            </p>
                            {event.relevance ? (
                                <div className="d-flex align-items-center">
                                    <StyledRating
                                        name="customized-color"
                                        defaultValue={
                                            event.relevance
                                                ? event.relevance.toLowerCase() === "low"
                                                    ? 1
                                                    : event.relevance.toLowerCase() === "medium"
                                                        ? 2
                                                        : 3
                                                : 0
                                        }
                                        max={3}
                                        disabled
                                        icon={<Square sx={{ fontSize: "16px" }} />}
                                        emptyIcon={<Square sx={{ fontSize: "16px" }} />}
                                    />
                                    <span className="ml-1 text-s text-capitalize">
                                        {event.relevance}
                                    </span>
                                </div>
                            ) : (
                                <span className="text-s">N/A</span>
                            )}
                        </div>
                        <div className="col-6 mb-3">
                            <p className="mb-0 text-sm">
                                <strong>Environment</strong>
                            </p>
                            <span className="text-s">{event.environment || "N/A"}</span>
                        </div>
                        <div className="col-6 mb-3">
                            <p className="mb-0 text-sm">
                                <strong>Platform</strong>
                            </p>
                            <span className="text-s">{event.platform || "N/A"}</span>
                        </div>
                        <div className="col-6 mb-3">
                            <p className="mb-0 text-sm">
                                <strong>Geo</strong>
                            </p>
                            <span className="text-s">{event.geo || "N/A"}</span>
                        </div>
                        <div className="col-6 mb-3">
                            <p className="mb-0 text-sm">
                                <strong>What Action Needed</strong>
                            </p>
                            <span className="text-s">{event.what_action_needed || "N/A"}</span>
                        </div>
                        {
                            !["On-Prem DB", "Venafi Certificates"].includes(event.Vendor_type) ? renderAdditionalData() : null
                        }
                    </div>
                    <Divider />
                    <div className="mt-3">
                        {event['Vendor_type'] === 'AWS Health' ? (
                            <div className="mb-4">
                                <p className="text-body-1 mb-2" >
                                    <strong>Summary <small>(Generated by Autodesk GPT)</small></strong>
                                </p>
                                {isLoaded ? (
                                    <div className="d-flex align-items-center" style={{ marginBottom: '16px' }}>
                                        <CircularProgress size={20} className="mr-2" />
                                        <span>Generating Summary...</span>
                                    </div>
                                ) : error ? (
                                    <p style={{ color: 'red' }}>{error}</p>
                                ) : (
                                    <div className="genai-summary" style={{ marginBottom: '16px' }}>
                                        {genaiSummary || ''}
                                    </div>
                                )}
                                <OriginalSummaryAccordion description={event['feature_delivered']} />
                            </div>
                        ) : event['Vendor_type'] === 'SaaS Vendor' && CUSTOM_SAP_SERVICES.includes(event["vendor_service"]) ? (
                            <div className="mb-4">
                                <p className="text-body-1 mb-2">
                                    <strong>Summary <small>(as received from SAP Subscribed Alerts)</small></strong>
                                </p>
                                <div
                                    className=""
                                    style={{ whiteSpace: "pre-wrap" }}
                                    dangerouslySetInnerHTML={{ __html: event.feature_delivered }}
                                ></div>
                            </div>
                        ) : (
                            <div className="mt-3">
                                <div className="mb-4">
                                    <p className="text-body-1 mb-2">
                                        <strong>Summary</strong>
                                    </p>
                                    <div
                                        className=""
                                        style={{ whiteSpace: "pre-wrap" }}
                                        dangerouslySetInnerHTML={{ __html: event.feature_delivered }}
                                    ></div>
                                </div>
                            </div>
                        )}
                        {history && (
                            <div>
                                <Divider />
                                <div className="d-flex justify-content-between mt-3">
                                    <p className="text-body-1 mb-3">
                                        <strong>Updates</strong>
                                    </p>
                                </div>
                                {Object.keys(history).map((key) => (
                                    <div
                                        className="border-left border-4 border-info pl-3"
                                        key={key}
                                    >
                                        <div className="d-flex justify-content-between align-items-center">
                                            <p className="badge badge-secondary text-capitalize mb-1 text-xs">
                                                {key}
                                            </p>
                                            <p className="text-sm mb-1">
                                                <strong>
                                                    {moment(history[key]["updatedAt"]).format(
                                                        "MMM DD, YYYY  HH:mm:SS"
                                                    )}
                                                </strong>
                                            </p>
                                        </div>
                                        <div
                                            className="mb-4 status"
                                            dangerouslySetInnerHTML={{ __html: history[key]["message"] }}
                                        ></div>
                                    </div>
                                ))}
                                <div className="d-flex align-items-center justify-content-around overflow-hidden mb-3">
                                    <Divider className="col-5" />
                                    <small className="mx-3 text-nowrap">End of Updates</small>
                                    <Divider className="col-5" />
                                </div>
                            </div>
                        )}
                    </div>
                </Box>
            </Drawer >
        ))
    );
};

export default EventDetails;
